/**
 * Geometry for the spread control: pushing devices further apart (or back together) so
 * labels and symbols stop colliding in a tight drawing.
 *
 * "could we provide a slider that allows the user to expand/contract the model? basically
 * e.g. 2x, 3x all pipe separations (not the length value)".
 *
 * ⚠️ This scales DIAGRAM POSITIONS only. `length` is a data field with no relationship to
 * where a symbol sits on the sheet, so spreading a drawing must never touch it — the
 * hydraulics are unchanged, only the picture. Symbol and text sizes are likewise untouched,
 * which is the entire point: zoom scales those too and therefore cannot relieve crowding,
 * whereas spreading positions alone opens real gaps.
 */

/** Smallest and largest spread the control offers. */
export const MIN_SPREAD = 0.5
export const MAX_SPREAD = 4

/** Treat spreads this close as equal, so a slider settling on its current value is a no-op. */
export const SPREAD_EPSILON = 1e-3

export interface Point { x: number; y: number }

/**
 * The fixed point of the scaling — the bounding-box centre of the drawing.
 *
 * Centre rather than centroid so a dense cluster at one end doesn't drag the whole layout
 * sideways as it spreads; the drawing grows evenly about its middle and stays roughly where
 * the user last had it on screen. Matches how the 3D viewer's spacing slider normalises.
 */
export function spreadOrigin(points: Point[]): Point {
  if (points.length === 0) return { x: 0, y: 0 }
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }
}

/** Move a point away from (or toward) the origin by `ratio`. */
export function scaleAbout(p: Point, origin: Point, ratio: number): Point {
  return {
    x: origin.x + (p.x - origin.x) * ratio,
    y: origin.y + (p.y - origin.y) * ratio,
  }
}

/**
 * The multiplier to apply RIGHT NOW to move a drawing already at `current` spread to
 * `target`.
 *
 * ⚠️ Positions are rewritten in place, so they already carry the spread applied so far.
 * Applying the target directly would compound — a model at 2× taken to 3× would end up at
 * 6×. The stored `diagram_spacing` exists precisely so this ratio can be derived.
 */
export function spreadRatio(current: number, target: number): number {
  if (!Number.isFinite(current) || current <= 0) return 1
  if (!Number.isFinite(target) || target <= 0) return 1
  return target / current
}

/** Is this a real change, or the slider landing back where it started? */
export function isSpreadNoop(current: number, target: number): boolean {
  return Math.abs(spreadRatio(current, target) - 1) < SPREAD_EPSILON
}

/** Clamp a requested spread into the range the control offers. */
export function clampSpread(value: number): number {
  if (!Number.isFinite(value)) return 1
  return Math.min(MAX_SPREAD, Math.max(MIN_SPREAD, value))
}
